import React, { useEffect, useState } from 'react';
import {
  Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormControlLabel,
  InputLabel, MenuItem, Radio, RadioGroup, Select, TextField, Typography,
} from '@mui/material';
import { rainPresetLabel, usesKostra } from './rainPresets.js';

const NO_TEMPLATE = '';

// Creating a scenario, optionally seeded from an existing one. Picking a
// template prefills the rain settings and, on save, copies the Maßnahmen of
// the source scenario across.
const ScenarioCreateDialog = ({ open, onClose, onCreate, scenarios, rainPresets }) => {
  const [name, setName] = useState('');
  const [templateId, setTemplateId] = useState(NO_TEMPLATE);
  const [selectedScenario, setSelectedScenario] = useState('light');
  const [rainAmount, setRainAmount] = useState(40);
  const [rainDuration, setRainDuration] = useState(3600);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName('');
    setTemplateId(NO_TEMPLATE);
    setSelectedScenario('light');
    setRainAmount(40);
    setRainDuration(3600);
    setSaving(false);
  }, [open]);

  const handleTemplateChange = (event) => {
    const value = event.target.value;
    setTemplateId(value);
    if (value === NO_TEMPLATE) return;
    const source = scenarios.find((s) => s.id === value);
    if (!source) return;
    setSelectedScenario(source.selected_scenario || 'custom');
    setRainAmount(source.rain_amount);
    setRainDuration(source.rain_duration);
    if (!name) setName(`${source.name} (Kopie)`);
  };

  const handleCreate = async () => {
    setSaving(true);
    await onCreate({
      name,
      selectedScenario,
      rainAmount,
      rainDuration,
      copyFromId: templateId === NO_TEMPLATE ? null : templateId,
    });
    setSaving(false);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Neues Szenario</DialogTitle>
      <DialogContent>
        <TextField
          label="Name"
          variant="outlined"
          fullWidth
          value={name}
          onChange={(e) => setName(e.target.value)}
          margin="dense"
        />

        <FormControl fullWidth margin="dense">
          <InputLabel id="scenario-template-label">Eigenschaften übernehmen von</InputLabel>
          <Select
            labelId="scenario-template-label"
            label="Eigenschaften übernehmen von"
            value={templateId}
            onChange={handleTemplateChange}
          >
            <MenuItem value={NO_TEMPLATE}>Keine Vorlage</MenuItem>
            {scenarios.map((s) => (
              <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
        {templateId !== NO_TEMPLATE && (
          <Typography variant="caption" color="text.secondary">
            Regeneinstellungen und Maßnahmen der Vorlage werden übernommen.
          </Typography>
        )}

        <RadioGroup
          value={selectedScenario}
          onChange={(e) => setSelectedScenario(e.target.value)}
          sx={{ mt: 1 }}
        >
          <FormControlLabel value="light" control={<Radio />} label={rainPresetLabel('light', rainPresets)} />
          <FormControlLabel value="heavy" control={<Radio />} label={rainPresetLabel('heavy', rainPresets)} />
          <FormControlLabel value="custom" control={<Radio />} label="Eigene Einstellungen" />
        </RadioGroup>

        {selectedScenario === 'custom' && (
          <>
            <TextField
              label="Regenmenge [mm/h]"
              type="number"
              inputProps={{ min: 0 }}
              variant="outlined"
              fullWidth
              margin="dense"
              value={rainAmount}
              onChange={(e) => setRainAmount(e.target.value)}
            />
            <TextField
              label="Regendauer [s]"
              type="number"
              inputProps={{ min: 0 }}
              variant="outlined"
              fullWidth
              margin="dense"
              value={rainDuration}
              onChange={(e) => setRainDuration(e.target.value)}
            />
          </>
        )}

        <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1.5 }}>
          {usesKostra(rainPresets)
            ? 'Die Regenmengen der Starkregenereignisse stammen aus KOSTRA-DWD-2020 für den '
              + 'Mittelpunkt der Region und gelten für eine Regendauer von 3600 s.'
            : 'Typische Werte für ein 30-jähriges Starkregenereignis sind 40 mm/h über 3600 s. In '
              + 'hügeligen Landschaften liegt die Regenmenge typischerweise etwa 25 % höher.'}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Abbrechen</Button>
        <Button variant="contained" onClick={handleCreate} disabled={!name || saving}>
          Erstellen
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ScenarioCreateDialog;
